import { ReactElement, useState } from 'react';
import { RearrangeIcon } from '../../bombiix-icon';

export interface BombiixReorderItem {
  id: string;
  label?: string;
  text: string;
}

export interface BombiixReorderListProps {
  items: BombiixReorderItem[];
  onReorder: (_items: BombiixReorderItem[]) => void;
  ariaLabel?: string | undefined;
  moveHint?: string | undefined;
  className?: string | undefined;
  testId?: string | undefined;
}

const HANDLE_ICON_SIZE = 24;

/**
 * Moves one entry of a list to a new index, returning a new array.
 * @param {BombiixReorderItem[]} items - The current order.
 * @param {number} from - Index being moved.
 * @param {number} to - Destination index.
 * @returns {BombiixReorderItem[]} The reordered list.
 */
export function moveItem(items: BombiixReorderItem[], from: number, to: number): BombiixReorderItem[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return items;
  }
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved as BombiixReorderItem);
  return next;
}

/**
 * Bombiix master-design reorder list (Figma node 13564:537594). Rows carry a
 * drag handle and reorder by pointer drag or by Arrow Up / Arrow Down while the
 * handle holds focus. Styling lives in
 * `assets/scss/master-design/_reorder-list.scss`.
 * @param {BombiixReorderListProps} props - Items plus the reorder callback.
 * @returns {ReactElement} The reorderable list.
 */
export function BombiixReorderList({
  items,
  onReorder,
  ariaLabel,
  moveHint,
  className,
  testId,
}: BombiixReorderListProps): ReactElement {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const finishDrag = (): void => {
    setDraggedIndex(null);
    setOverIndex(null);
  };

  const handleDrop = (targetIndex: number): void => {
    if (draggedIndex !== null && draggedIndex !== targetIndex) {
      onReorder(moveItem(items, draggedIndex, targetIndex));
    }
    finishDrag();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number): void => {
    const offset = event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0;
    if (offset === 0) {
      return;
    }
    event.preventDefault();
    onReorder(moveItem(items, index, index + offset));
  };

  return (
    <ul className={['bombiix-reorder-list', className].filter(Boolean).join(' ')} aria-label={ariaLabel} data-testid={testId}>
      {items.map((item, index) => (
        <li
          key={item.id}
          className={[
            'bombiix-reorder-list__row',
            draggedIndex === index ? 'bombiix-reorder-list__row--dragging' : '',
            overIndex === index && draggedIndex !== index ? 'bombiix-reorder-list__row--over' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          draggable
          onDragStart={() => setDraggedIndex(index)}
          onDragOver={(event) => {
            event.preventDefault();
            setOverIndex(index);
          }}
          onDrop={() => handleDrop(index)}
          onDragEnd={finishDrag}
        >
          <button
            type="button"
            className="bombiix-reorder-list__handle"
            aria-label={moveHint ? `${moveHint}: ${item.text}` : item.text}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            <RearrangeIcon size={HANDLE_ICON_SIZE} />
          </button>
          <span className="bombiix-reorder-list__divider" aria-hidden="true" />
          {item.label ? <span className="bombiix-reorder-list__label">{item.label}</span> : null}
          <span className="bombiix-reorder-list__text">{item.text}</span>
        </li>
      ))}
    </ul>
  );
}

export default BombiixReorderList;
